/**
 * Rule shapes and wording for the automation editor. A rule's `definition` is exactly what the
 * backend's portable core expects: { trigger, conditions, actions }.
 */
const NUMERIC = ['NUMBER', 'DECIMAL', 'CURRENCY', 'ROLLUP']
const TEXT = ['TEXT', 'TEXTAREA', 'EMAIL', 'PHONE', 'URL']

export const TRIGGERS = [
  { type: 'CREATED', label: 'is created' },
  { type: 'UPDATED', label: 'is updated' },
  { type: 'CREATED_OR_UPDATED', label: 'is created or updated' },
  { type: 'FIELD_CHANGED', label: 'has a field change' },
  { type: 'DELETED', label: 'is deleted' },
  { type: 'DATE_REACHED', label: 'reaches a date', timeBased: true },
  { type: 'SCHEDULE', label: 'on a schedule', timeBased: true },
]
export const isTimeBased = (trigger) => trigger?.type === 'DATE_REACHED' || trigger?.type === 'SCHEDULE'

export const EVERY = [{ value: 'DAILY', label: 'Every day' }, { value: 'WEEKLY', label: 'Every week' }, { value: 'MONTHLY', label: 'Every month' }]
export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** Record dates that time-based rules can test ("not updated for 7 days"); compared with $today±N. */
export const SYSTEM_DATES = [
  { fieldKey: '$updatedOn', label: 'Last updated', fieldType: 'DATETIME', system: true },
  { fieldKey: '$createdOn', label: 'Created', fieldType: 'DATETIME', system: true },
]

export const ACTIONS = [
  { type: 'UPDATE_RECORD', label: 'Update this record', hint: 'Set fields on the record that triggered the rule' },
  { type: 'CREATE_RECORD', label: 'Create a record', hint: 'For example a follow-up task linked to this record' },
  { type: 'NOTIFY', label: 'Send a notification', hint: 'In-app notification to people in this workspace' },
  { type: 'CREATE_TASK', label: 'Create a task', hint: 'A to-do on this record for a person, with a due date and reminder' },
  { type: 'SEND_EMAIL', label: 'Send an email', hint: 'From this record, to its email or a person; logged on its timeline', needsEmail: true },
  { type: 'ASSIGN_ROUND_ROBIN', label: 'Assign in turn', hint: 'Fill a person field with the next member in the rotation' },
]
export const actionLabel = (type) => ACTIONS.find((a) => a.type === type)?.label || type

export const OP_LABELS = {
  eq: 'is', neq: 'is not', gt: 'is more than', gte: 'is at least', lt: 'is less than', lte: 'is at most',
  contains: 'contains', empty: 'is empty', notEmpty: 'is not empty',
}
export const UNARY = ['empty', 'notEmpty']

export const opsFor = (field) => {
  const t = field?.fieldType
  if (t === 'MULTI_SELECT') return ['contains', 'empty', 'notEmpty']
  if (t === 'CHECKBOX') return ['eq']
  const ordered = NUMERIC.includes(t) || t === 'DATE' || t === 'DATETIME'
  return ['eq', 'neq', ...(ordered ? ['gt', 'gte', 'lt', 'lte'] : []), ...(TEXT.includes(t) ? ['contains'] : []), 'empty', 'notEmpty']
}

export const isText = (field) => TEXT.includes(field?.fieldType)

export const TODAY_TOKEN = /^\$today([+-]\d+)?$/
export const todayOffset = (value) => Number((TODAY_TOKEN.exec(value) || [])[1] || 0)
export const todayToken = (days) => (days ? `$today${days > 0 ? '+' : ''}${days}` : '$today')

/** Same type (any text type counts as one), so a copied value always fits. */
const compatible = (from, to) => from.fieldType === to.fieldType || (isText(from) && isText(to))

/**
 * Dynamic values a field can take besides a fixed one.
 * @param ctx.entityType the triggering record type  @param ctx.sourceFields its fields
 */
export const tokenOptions = (field, ctx) => {
  const { entityType, sourceFields = [] } = ctx
  const t = field.fieldType
  const out = []
  if (t === 'DATE' || t === 'DATETIME') out.push({ value: '$today', label: 'Today (± days)' })
  if (t === 'DATETIME') out.push({ value: '$now', label: 'Now' })
  if (t === 'USER' && !ctx.timeBased) out.push({ value: '$actor', label: 'Person who made the change' })
  if (t === 'LOOKUP' && field.lookupEntityType === entityType) out.push({ value: '$record', label: 'The triggering record' })
  sourceFields.filter((f) => compatible(f, field)).forEach((f) => out.push({ value: `$field:${f.fieldKey}`, label: `Copy “${f.label}”` }))
  return out
}

/** Short text for a configured value, tokens included. `fixed(field, value)` formats plain values. */
export const describeValue = (field, value, sourceByKey, fixed) => {
  if (typeof value === 'string' && value.startsWith('$')) {
    if (TODAY_TOKEN.test(value)) {
      const days = todayOffset(value)
      return days ? `today ${days > 0 ? '+' : '−'} ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}` : 'today'
    }
    if (value === '$now') return 'now'
    if (value === '$actor') return 'the person who made the change'
    if (value === '$record') return 'this record'
    if (value.startsWith('$field:')) return `its ${sourceByKey[value.slice(7)]?.label || value.slice(7)}`
  }
  return field ? fixed(field, value) : String(value)
}

export const newAction = (type) => {
  if (type === 'UPDATE_RECORD') return { type, params: { values: {} } }
  if (type === 'CREATE_RECORD') return { type, params: { formCode: '', values: {} } }
  if (type === 'CREATE_TASK') return { type, params: { title: '', assignee: '', dueInDays: 1, dueTime: '10:00' } }
  if (type === 'SEND_EMAIL') return { type, params: { to: [], subject: '', body: '' } }
  if (type === 'ASSIGN_ROUND_ROBIN') return { type, params: { field: '', members: [], onlyIfEmpty: true } }
  return { type, params: { recipients: [], title: '', message: '' } }
}

export const emptyRule = (entityType = '') => ({
  name: '',
  entityType,
  enabled: true,
  definition: { trigger: { type: 'CREATED' }, conditions: [], actions: [] },
})

/** Drop settings that no longer apply after the record type or trigger changed. */
export const cleanTrigger = (trigger) => {
  if (trigger.type === 'FIELD_CHANGED') return { type: trigger.type, field: trigger.field || null, to: trigger.to ?? null }
  if (trigger.type === 'DATE_REACHED') return { type: trigger.type, field: trigger.field || null, offsetDays: trigger.offsetDays ?? 0, at: trigger.at || '09:00' }
  if (trigger.type === 'SCHEDULE') {
    const every = trigger.every || 'DAILY'
    return {
      type: trigger.type, every, at: trigger.at || '09:00',
      ...(every === 'WEEKLY' ? { dayOfWeek: trigger.dayOfWeek || 1 } : {}),
      ...(every === 'MONTHLY' ? { dayOfMonth: trigger.dayOfMonth || 1 } : {}),
    }
  }
  return { type: trigger.type }
}

/** "1 day before Close date at 09:00", "every Monday at 09:00" */
export const describeTiming = (trigger, label) => {
  if (trigger.type === 'DATE_REACHED') {
    const d = trigger.offsetDays || 0
    const when = d === 0 ? `on its ${label(trigger.field)}` : `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ${d < 0 ? 'before' : 'after'} its ${label(trigger.field)}`
    return `${when}${trigger.at ? ` (at ${trigger.at} for dates without a time)` : ''}`
  }
  const day = trigger.every === 'WEEKLY' ? `every ${WEEKDAYS[(trigger.dayOfWeek || 1) - 1]}`
    : trigger.every === 'MONTHLY' ? `on day ${trigger.dayOfMonth || 1} of every month` : 'every day'
  return `${day} at ${trigger.at || '09:00'}`
}
